import { forwardRef, type HTMLAttributes } from 'react';
import * as RTooltip from '@radix-ui/react-tooltip';
import { cn } from '../../lib/cn';
import { type SColor } from '../../lib/color';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { SIcon, type SIconName } from '../SIcon';
import { STag } from '../STag';

export type STextLinkSize = 'sm' | 'md' | 'lg';
export type STextLinkArrow = 'none' | 'chevron' | 'caret';

const SIZE_CONFIG: Record<
  STextLinkSize,
  { gap: number; iconSize: number; typo: string; underlineTypo: string }
> = {
  sm: {
    gap: 4,
    iconSize: 16,
    typo: 'typo-control-xs-medium',
    underlineTypo: 'typo-control-xs-underline',
  },
  md: {
    gap: 8,
    iconSize: 16,
    typo: 'typo-control-sm-default',
    underlineTypo: 'typo-control-sm-underline',
  },
  lg: {
    gap: 8,
    iconSize: 20,
    typo: 'typo-control-md-default',
    underlineTypo: 'typo-control-md-underline',
  },
};

export interface STextLinkProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  /** 레이블 */
  label?: string;
  /** 좌측 아이콘 */
  icon?: SIconName;
  /** 좌측 아이콘 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상 */
  iconColor?: SColor;
  /** 레이블 span에 추가할 클래스 */
  labelClass?: string;
  /** 우측 화살표 */
  rightArrow?: STextLinkArrow;
  /** 밑줄 여부 */
  underline?: boolean;
  /** 비활성 */
  disabled?: boolean;
  /** 크기 */
  size?: STextLinkSize;
  /** 호버 시 표시할 툴팁 텍스트 (회색 STag 스타일) */
  tooltipText?: string;
  onClick?: () => void;
}

/** STextLink — sd-text-link 포팅. 아이콘·화살표·밑줄·크기 지원. */
export const STextLink = forwardRef<HTMLSpanElement, STextLinkProps>(function STextLink(
  {
    label = '',
    icon,
    iconColor,
    labelClass,
    rightArrow = 'none',
    underline = false,
    disabled = false,
    size = 'sm',
    tooltipText,
    className,
    onClick,
    children,
    style,
    ...rest
  },
  ref,
) {
  const s = SIZE_CONFIG[size];
  const contentColor = disabled
    ? 'var(--cmp-textLink-content-disabled)'
    : 'var(--cmp-textLink-content-default)';
  const resolvedIconColor = disabled
    ? 'var(--cmp-textLink-icon-disabled)'
    : iconColor || 'var(--cmp-textLink-icon-default)';

  const handle = () => {
    if (!disabled) onClick?.();
  };

  const portalContainer = usePortalContainer();

  const link = (
    <span
      ref={ref}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handle();
        }
      }}
      className={cn(
        'inline-flex cursor-pointer items-center',
        underline ? s.underlineTypo : s.typo,
        !disabled && 'hover:underline',
        disabled && 'cursor-not-allowed',
        className,
      )}
      style={{ gap: s.gap, color: contentColor, ...style }}
      {...rest}
    >
      {icon && <SIcon name={icon} size={s.iconSize} color={resolvedIconColor} />}
      {/* disabled일 때는 labelClass와 무관하게 disabled 색으로 덮어씀 (인라인 style이 className을 이김) */}
      <span
        className={cn(labelClass)}
        style={disabled ? { color: 'var(--cmp-textLink-content-disabled)' } : undefined}
      >
        {label || children}
      </span>
      {rightArrow !== 'none' && (
        <span className="inline-flex h-[16px] w-[16px] items-center justify-center">
          <SIcon
            name={rightArrow === 'chevron' ? 'chevronRight' : 'caretRight'}
            size={12}
            color={disabled ? 'var(--cmp-textLink-icon-disabled)' : 'var(--color-grey-65)'}
          />
        </span>
      )}
    </span>
  );

  const text = (tooltipText ?? '').trim();
  if (text === '') return link;

  return (
    <RTooltip.Provider delayDuration={200}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{link}</RTooltip.Trigger>
        <RTooltip.Portal container={portalContainer}>
          <RTooltip.Content side="top" sideOffset={4} className={cn('z-50', FLOATING_SLIDE_ANIM)}>
            <STag shape="square" size="xs" color="grey" label={text} />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
});
